/**
 * Normalize API / Redux errors into a string safe for toast and UI rendering.
 * Passing raw objects to React children crashes the app (blank white screen).
 */

const FIELD_LABELS = {
  username: 'Username',
  email: 'Email',
  password: 'Password',
  phone_number: 'Phone number',
  first_name: 'First name',
  last_name: 'Last name',
  referral_code: 'Referral code',
  shipping_address: 'Shipping address',
  non_field_errors: '',
};

function fieldLabel(key) {
  if (Object.prototype.hasOwnProperty.call(FIELD_LABELS, key)) {
    return FIELD_LABELS[key];
  }
  return key.replace(/_/g, ' ');
}

function formatFieldErrors(details) {
  if (!details || typeof details !== 'object' || Array.isArray(details)) return '';

  const messages = Object.entries(details).flatMap(([key, value]) => {
    const label = fieldLabel(key);
    const values = Array.isArray(value) ? value : [value];
    return values
      .map((msg) => {
        if (msg == null || msg === '') return '';
        if (typeof msg === 'object') {
          return formatUserFacingError(msg, '');
        }
        const text = String(msg);
        return label ? `${label}: ${text}` : text;
      })
      .filter(Boolean);
  });

  return messages.join('\n');
}

export function formatUserFacingError(error, fallback = 'Something went wrong') {
  if (error == null || error === '') return fallback;
  if (typeof error === 'string') {
    if (/network error/i.test(error)) {
      return "Couldn't reach the server. Stay on this page and try again.";
    }
    return error;
  }
  if (error?.userMessage && typeof error.userMessage === 'string') {
    return error.userMessage;
  }
  if (
    error?.code === 'ERR_NETWORK' ||
    error?.code === 'ECONNABORTED' ||
    /network error/i.test(String(error?.message || ''))
  ) {
    return "Couldn't reach the server. Stay on this page and try again.";
  }

  if (typeof error === 'object') {
    // Prefer nested validation/field details over generic wrappers like
    // { error: "Registration failed", details: { username: ["..."] } }
    if (error.details != null) {
      const details = error.details;
      if (typeof details === 'string' && details.trim()) return details;
      if (Array.isArray(details)) {
        const joined = details
          .map((item) => formatUserFacingError(item, ''))
          .filter(Boolean)
          .join('\n');
        if (joined) return joined;
      }
      if (typeof details === 'object') {
        const fromDetails = formatFieldErrors(details);
        if (fromDetails) return fromDetails;
      }
    }

    if (typeof error.error === 'string' && error.error.trim()) {
      // Skip generic wrappers when details exist (handled above when present)
      const genericWrappers = new Set([
        'Registration failed',
        'Please fix the highlighted fields and try again.',
      ]);
      if (!genericWrappers.has(error.error) || !error.details) {
        return error.error;
      }
    }
    if (typeof error.message === 'string' && error.message.trim()) return error.message;
    if (typeof error.detail === 'string' && error.detail.trim()) return error.detail;

    if (Array.isArray(error.detail)) {
      return error.detail.map((item) => formatUserFacingError(item, '')).filter(Boolean).join('\n') || fallback;
    }

    const fieldMessages = formatFieldErrors(error);
    if (fieldMessages) return fieldMessages;
  }

  try {
    return JSON.stringify(error);
  } catch {
    return fallback;
  }
}
